import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { Button, Card, Field, Input, Textarea, Select, Modal, Note, Empty, Skeleton } from '../ui';

/** Платформ админ: sainhursh.mn/ppt хуудасны «Асуудал ↔ Шийдэл» хүснэгтийг бүлгээр нь засварлана. */
type Item = { id: string; no: number; group: string; issue: string; solution: string };
type Group = { group: string; count: number; items: Item[] };

export default function PptContentPage() {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [names, setNames] = useState<string[]>([]);
  const [tab, setTab] = useState(0);
  const [dlg, setDlg] = useState<null | { id?: string; group: string; issue: string; solution: string }>(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await api.get<any>('/admin/ppt/items');
    setGroups(r.groups || []);
    setNames(r.group_names || []);
  }, []);
  useEffect(() => { load().catch(() => setGroups([])); }, [load]);

  const flash = (t: string) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };
  const err = (e: any) => alert(e?.body ? (JSON.parse(e.body)?.detail || 'Алдаа') : (e?.message || 'Алдаа'));

  const save = async () => {
    if (!dlg) return;
    if (!dlg.issue.trim()) { alert('Асуудлыг бичнэ үү'); return; }
    setBusy('save');
    try {
      if (dlg.id) await api.put(`/admin/ppt/items/${dlg.id}`, { group: dlg.group, issue: dlg.issue, solution: dlg.solution });
      else await api.post('/admin/ppt/items', { group: dlg.group, issue: dlg.issue, solution: dlg.solution });
      setDlg(null); await load(); flash(dlg.id ? 'Хадгаллаа' : 'Нэмэгдлээ');
    } catch (e) { err(e); } finally { setBusy(''); }
  };
  const remove = async (it: Item) => {
    if (!window.confirm(`${it.no}-р мөрийг устгах уу?\n\n«${it.issue.slice(0, 80)}»`)) return;
    setBusy(it.id);
    try { await api.delete(`/admin/ppt/items/${it.id}`); await load(); flash('Устлаа'); }
    catch (e) { err(e); } finally { setBusy(''); }
  };
  const move = async (it: Item, dir: 'up' | 'down') => {
    setBusy(it.id);
    try { await api.post(`/admin/ppt/items/${it.id}/move?dir=${dir}`, {}); await load(); }
    catch (e) { err(e); } finally { setBusy(''); }
  };

  const g = groups?.[tab];
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Танилцуулга хуудас (/ppt)</h2>
        <a href="https://sainhursh.mn/ppt/" target="_blank" rel="noreferrer" style={{ fontSize: 13.5, color: 'var(--green)' }}>sainhursh.mn/ppt харах ↗</a>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {msg && <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>✓ {msg}</span>}
          <Button icon="plus" onClick={() => setDlg({ group: g?.group || names[0] || '', issue: '', solution: '' })}>Мөр нэмэх</Button>
        </div>
      </div>
      <Note>Энэ хүснэгт нь <b>sainhursh.mn/ppt</b> хуудсанд шууд харагдана. Мөрийг засах, устгах, дарааллыг дээш/доош шилжүүлэхэд нийтийн хуудас тэр даруй шинэчлэгдэнэ (дугаарлалт автоматаар дахин тоологдоно).</Note>

      {groups === null ? <Skeleton h={200} style={{ marginTop: 14 }} /> : groups.length === 0 ? <Empty title="Мөр алга" text="«Мөр нэмэх» дарж эхлүүлнэ үү." /> : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
            {groups.map((x, i) => (
              <button key={x.group} onClick={() => setTab(i)} style={{
                padding: '8px 14px', borderRadius: 9, cursor: 'pointer', font: 'inherit', fontWeight: 600,
                border: `2px solid ${i === tab ? 'var(--green)' : 'var(--line)'}`,
                background: i === tab ? 'var(--green-bg, #eaf4ee)' : 'var(--card, #fff)', color: 'inherit',
              }}>{i + 1}. {x.group} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({x.count})</span></button>
            ))}
          </div>
          <Card style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ background: 'var(--bg-soft, #f8fafc)', color: 'var(--muted)' }}>
                <th style={{ ...th, width: 46, textAlign: 'center' }}>№</th>
                <th style={th}>Тулгамдсан асуудал</th>
                <th style={th}>Сайн хөрш системийн шийдэл</th>
                <th style={{ ...th, width: 150 }}></th>
              </tr></thead>
              <tbody>{(g?.items || []).map((it, i) => (
                <tr key={it.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: 'var(--muted)' }}>{it.no}</td>
                  <td style={{ ...td, borderLeft: '4px solid var(--red)' }}>{it.issue}</td>
                  <td style={{ ...td, borderLeft: '4px solid var(--green)' }}>{it.solution || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <Button size="sm" variant="ghost" disabled={i === 0 || busy === it.id} onClick={() => move(it, 'up')} aria-label="Дээш">↑</Button>
                    <Button size="sm" variant="ghost" disabled={i === (g?.items.length || 0) - 1 || busy === it.id} onClick={() => move(it, 'down')} aria-label="Доош">↓</Button>
                    <Button size="sm" variant="ghost" icon="edit" onClick={() => setDlg({ id: it.id, group: it.group, issue: it.issue, solution: it.solution })} aria-label="Засах" />
                    <Button size="sm" variant="ghost" loading={busy === it.id} style={{ color: 'var(--red)' }} onClick={() => remove(it)}>Устгах</Button>
                  </td>
                </tr>))}</tbody>
            </table>
          </Card>
        </>
      )}

      {dlg && (
        <Modal title={dlg.id ? 'Мөр засах' : 'Шинэ мөр'} width={680} onClose={() => setDlg(null)}
          footer={<><Button variant="ghost" onClick={() => setDlg(null)}>Болих</Button><Button loading={busy === 'save'} onClick={save}>Хадгалах</Button></>}>
          <Field label="Бүлэг" id="pp-g"><Select id="pp-g" value={dlg.group} onChange={e => setDlg({ ...dlg, group: e.target.value })}>
            {names.map(n => <option key={n} value={n}>{n}</option>)}
          </Select></Field>
          <Field label="Тулгамдсан асуудал" id="pp-i"><Textarea id="pp-i" rows={4} value={dlg.issue} onChange={e => setDlg({ ...dlg, issue: e.target.value })} placeholder="Жишээ: Нэхэмжлэлийг хаалганд нааж, цаасаар тараадаг" /></Field>
          <Field label="Сайн хөрш системийн шийдэл" id="pp-s"><Textarea id="pp-s" rows={4} value={dlg.solution} onChange={e => setDlg({ ...dlg, solution: e.target.value })} placeholder="Жишээ: Нэхэмжлэл сар бүр автоматаар үүсэж, QR уншуулахад шинэчлэгдэнэ." /></Field>
        </Modal>
      )}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', fontSize: 13, fontWeight: 600 };
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top', lineHeight: 1.45 };
